import { useEffect, useState, useMemo } from "react";
import api, { brl, LOGO_URL } from "../lib/api";
import UpClubBanner from "../components/UpClubBanner";
import {
  ShoppingBag, Phone, Instagram, MapPin, Wrench, Smartphone, Search, X,
  ChevronLeft, ChevronRight, Menu, ShoppingCart,
} from "lucide-react";

const BANNERS = [
  { img: "/api/static/products/pel_privacidade.png", title: "Películas de Privacidade", subtitle: "Proteção + Privacidade para seu celular", cat: "Películas" },
  { img: "/api/static/products/capa_cover_iphone.png", title: "Capas Cover Protector", subtitle: "iPhone, Samsung, Motorola e Xiaomi", cat: "Capas" },
  { img: "/api/static/products/power_bank.png", title: "Power Banks", subtitle: "Carregue seu celular em qualquer lugar", cat: "Carregadores e Cabos" },
  { img: "/api/static/products/fone_tws.png", title: "Fones Sem Fio", subtitle: "TWS, Bluetooth e Gamer RGB", cat: "Áudio" },
  { img: "/api/static/products/capa_magsafe.png", title: "Capas TPU MagSafe Transparente", subtitle: "Tecnologia magnética + visual clean", cat: "Capas" },
  { img: "/api/static/products/smartwatch.png", title: "Smartwatches", subtitle: "Acompanhe sua saúde e notificações", cat: "Smartwatch" },
  { img: "/api/static/products/console_retro.png", title: "Games & Controles", subtitle: "Console retrô e acessórios gamer", cat: "Games" },
  { img: "/api/static/products/pendrive.png", title: "Pen Drives & Cartões", subtitle: "Armazenamento para todos os dispositivos", cat: "Armazenamento" },
];

export default function Catalog() {
  const [products, setProducts] = useState([]);
  const [settings, setSettings] = useState({});
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("all");
  const [bannerIdx, setBannerIdx] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    api.get("/public/catalog").then((r) => setProducts(r.data));
    api.get("/public/settings").then((r) => setSettings(r.data));
  }, []);

  // Auto-rotate banner
  useEffect(() => {
    const t = setInterval(() => setBannerIdx((i) => (i + 1) % BANNERS.length), 5000);
    return () => clearInterval(t);
  }, []);

  const wa = (msg) => {
    const num = (settings.whatsapp_number || "").replace(/\D/g, "");
    return `https://wa.me/${num}?text=${encodeURIComponent(msg)}`;
  };

  const categories = useMemo(() => {
    const map = {};
    products.forEach((p) => { if (p.category) map[p.category] = (map[p.category] || 0) + 1; });
    return Object.entries(map).sort((a, b) => b[1] - a[1]).map(([name, count]) => ({ name, count }));
  }, [products]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return products.filter((p) => {
      if (category !== "all" && p.category !== category) return false;
      if (!term) return true;
      return (
        p.name.toLowerCase().includes(term) ||
        (p.sku || "").toLowerCase().includes(term) ||
        (p.category || "").toLowerCase().includes(term)
      );
    });
  }, [products, q, category]);

  // Last-created products are shown with "NOVIDADE" badge (top 40)
  const novidadeIds = useMemo(() => {
    const sorted = [...products].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    return new Set(sorted.slice(0, 40).map((p) => p.id));
  }, [products]);

  const openCat = (cat) => { setCategory(cat); setMenuOpen(false); window.scrollTo({ top: 550, behavior: "smooth" }); };

  return (
    <div className="min-h-screen bg-white text-slate-900">
      {/* Top bar */}
      <div className="bg-[#0A1628] text-white text-xs">
        <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between">
          <div className="flex items-center gap-4 opacity-90">
            <span className="hidden md:inline">📞 {settings.phone || "(49) 99812-2884"}</span>
            {settings.address && <span className="hidden lg:inline">📍 {settings.address}</span>}
          </div>
          <div className="flex items-center gap-3">
            {settings.instagram_handle && (
              <a href={`https://instagram.com/${settings.instagram_handle}`} target="_blank" rel="noreferrer" data-testid="catalog-instagram-link" className="flex items-center gap-1 hover:text-blue-300">
                <Instagram size={12} /> @{settings.instagram_handle}
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Header */}
      <header className="sticky top-0 z-40 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-4">
          <button className="lg:hidden" onClick={() => setMenuOpen(!menuOpen)} data-testid="catalog-menu-btn">
            <Menu size={22} />
          </button>
          <a href="/catalog" className="flex items-center gap-2 shrink-0">
              <img src={settings.store_logo_url || LOGO_URL} alt="UP CEL Quilombo" className="w-11 h-11 rounded-sm object-cover" />
            <div className="hidden sm:block">
              <div className="font-display font-bold text-xl leading-none">{settings.store_name || "UP CEL Quilombo"}</div>
              <div className="text-[9px] uppercase tracking-[0.3em] text-slate-500 mt-0.5">Celulares & Acessórios</div>
            </div>
          </a>
          <div className="flex-1 relative max-w-2xl mx-auto">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="O que você procura?"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="catalog-search-input"
              className="w-full pl-10 pr-10 py-2.5 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-[#2563EB] focus:border-[#2563EB] bg-slate-50 focus:bg-white"
            />
            {q && (
              <button onClick={() => setQ("")} data-testid="catalog-search-clear" className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700">
                <X size={16} />
              </button>
            )}
          </div>
          {settings.whatsapp_number && (
            <a href={wa("Olá! Vim pelo catálogo.")} target="_blank" rel="noreferrer" data-testid="catalog-whatsapp-link" className="hidden md:flex items-center gap-1.5 bg-emerald-500 hover:bg-emerald-600 text-white px-4 py-2.5 rounded-sm text-sm font-semibold">
              <Phone size={14} /> WhatsApp
            </a>
          )}
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 pt-4">
        <UpClubBanner />
      </div>

      <div className="max-w-7xl mx-auto px-4 py-4 grid lg:grid-cols-[240px_1fr] gap-6">
        {/* Sidebar menu */}
        <aside className={`lg:block ${menuOpen ? "block" : "hidden"}`}>
          <div className="bg-slate-50 border border-slate-200 rounded-sm overflow-hidden sticky top-[90px]">
            <div className="bg-[#0A1628] text-white px-4 py-3 flex items-center gap-2 font-semibold text-sm uppercase tracking-wider">
              <Menu size={16} /> Categorias
            </div>
            <ul className="text-sm">
              <li>
                <button
                  onClick={() => openCat("all")}
                  data-testid="catalog-cat-all"
                  className={`w-full text-left px-4 py-2.5 flex justify-between items-center border-b border-slate-200 hover:bg-blue-50 hover:text-[#2563EB] ${category === "all" ? "bg-blue-50 text-[#2563EB] font-semibold" : ""}`}
                >
                  <span>Todos os Produtos</span>
                  <span className="text-xs text-slate-500">{products.length}</span>
                </button>
              </li>
              {categories.map((c) => (
                <li key={c.name}>
                  <button
                    onClick={() => openCat(c.name)}
                    data-testid={`catalog-cat-${c.name}`}
                    className={`w-full text-left px-4 py-2.5 flex justify-between items-center border-b border-slate-200 hover:bg-blue-50 hover:text-[#2563EB] ${category === c.name ? "bg-blue-50 text-[#2563EB] font-semibold" : ""}`}
                  >
                    <span className="truncate pr-2">{c.name}</span>
                    <span className="text-xs text-slate-500 shrink-0">{c.count}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </aside>

        {/* Main content */}
        <main>
          {/* Services CTA - moved to top */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
            <div className="bg-[#0A1628] text-white rounded-sm p-4 flex items-center gap-3">
              <div className="w-11 h-11 rounded-sm bg-[#2563EB]/20 flex items-center justify-center shrink-0">
                <Wrench className="text-blue-400" size={20} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display font-bold text-base leading-tight">Assistência Técnica</div>
                <div className="text-xs text-slate-300 mt-0.5">Orçamento rápido de reparo</div>
              </div>
              {settings.whatsapp_number && (
                <a href={wa("Olá, gostaria de orçamento para assistência técnica.")} target="_blank" rel="noreferrer"
                   data-testid="catalog-service-btn"
                   className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white px-3 py-2 rounded-sm text-xs font-semibold whitespace-nowrap">
                  Pedir orçamento
                </a>
              )}
            </div>
            <div className="bg-[#0A1628] text-white rounded-sm p-4 flex items-center gap-3">
              <div className="w-11 h-11 rounded-sm bg-emerald-500/20 flex items-center justify-center shrink-0">
                <Phone className="text-emerald-400" size={20} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display font-bold text-base leading-tight">Celular no WhatsApp</div>
                <div className="text-xs text-slate-300 mt-0.5">Fale direto com a loja</div>
              </div>
              {settings.whatsapp_number && (
                <a href={wa("Olá! Vim pelo catálogo da UP CEL Quilombo.")} target="_blank" rel="noreferrer"
                   data-testid="catalog-accessories-btn"
                   className="bg-emerald-500 hover:bg-emerald-600 text-white px-3 py-2 rounded-sm text-xs font-semibold whitespace-nowrap flex items-center gap-1">
                  <Phone size={12} /> WhatsApp
                </a>
              )}
            </div>
          </div>

          {/* Banner carousel */}
          <div className="relative rounded-sm overflow-hidden bg-gradient-to-br from-[#0A1628] to-[#0B1F3A] mb-6 h-[220px] md:h-[300px]" data-testid="catalog-banner">
            {BANNERS.map((b, i) => (
              <button
                key={i}
                onClick={() => openCat(b.cat)}
                data-testid={`catalog-banner-${i}`}
                className={`absolute inset-0 flex items-center justify-between px-6 md:px-12 transition-opacity duration-700 ${i === bannerIdx ? "opacity-100" : "opacity-0 pointer-events-none"}`}
              >
                <div className="relative z-10 text-left max-w-md">
                  <div className="text-[10px] uppercase tracking-[0.3em] text-blue-300 mb-2 font-semibold">OFERTA UP CEL QUILOMBO</div>
                  <h2 className="font-display text-2xl md:text-4xl font-bold text-white leading-tight">{b.title}</h2>
                  <p className="text-slate-300 text-sm md:text-base mt-2">{b.subtitle}</p>
                  <span className="inline-block mt-4 bg-[#2563EB] text-white text-xs font-semibold px-4 py-2 rounded-sm">Ver produtos →</span>
                </div>
                <img src={b.img} alt={b.title} className="absolute right-0 top-0 h-full w-[50%] md:w-[40%] object-cover opacity-70 md:opacity-100" style={{ maskImage: "linear-gradient(to left, black 60%, transparent)", WebkitMaskImage: "linear-gradient(to left, black 60%, transparent)" }} />
              </button>
            ))}
            {/* Arrows */}
            <button
              onClick={() => setBannerIdx((i) => (i - 1 + BANNERS.length) % BANNERS.length)}
              data-testid="catalog-banner-prev"
              className="absolute left-2 top-1/2 -translate-y-1/2 z-20 bg-white/20 hover:bg-white/40 text-white p-2 rounded-sm backdrop-blur"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              onClick={() => setBannerIdx((i) => (i + 1) % BANNERS.length)}
              data-testid="catalog-banner-next"
              className="absolute right-2 top-1/2 -translate-y-1/2 z-20 bg-white/20 hover:bg-white/40 text-white p-2 rounded-sm backdrop-blur"
            >
              <ChevronRight size={18} />
            </button>
            {/* Dots */}
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 z-20">
              {BANNERS.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setBannerIdx(i)}
                  data-testid={`catalog-banner-dot-${i}`}
                  className={`h-1.5 rounded-full transition-all ${i === bannerIdx ? "bg-white w-6" : "bg-white/40 w-1.5"}`}
                />
              ))}
            </div>
          </div>

          {/* Section title */}
          <div className="flex items-end justify-between mb-4 flex-wrap gap-3">
            <div>
              <h3 className="font-display text-2xl font-bold border-b-2 border-[#2563EB] inline-block pb-1 pr-6">
                {category === "all" ? (q ? `Resultados para "${q}"` : "Produtos") : category}
              </h3>
              {category !== "all" && (
                <button onClick={() => setCategory("all")} data-testid="catalog-clear-category" className="ml-2 text-xs text-[#2563EB] hover:underline">
                  Limpar filtro
                </button>
              )}
            </div>
            <span className="text-sm text-slate-500">{filtered.length} produtos</span>
          </div>

          {filtered.length === 0 ? (
            <div className="text-center py-20 text-slate-500 bg-slate-50 rounded-sm border border-slate-200">
              <Search className="mx-auto mb-3 text-slate-300" size={40} />
              <div className="font-semibold">Nenhum produto encontrado</div>
              <div className="text-sm mt-1">Tente outra busca ou categoria.</div>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4 xl:grid-cols-5 gap-3">
              {filtered.map((p) => (
                <div key={p.id} className="border border-slate-200 rounded-sm overflow-hidden bg-white hover:shadow-lg hover:border-[#2563EB] transition-all flex flex-col group" data-testid={`catalog-product-${p.id}`}>
                  <div className="aspect-square bg-white relative overflow-hidden border-b border-slate-100">
                    {p.image_url ? (
                      <img src={p.image_url} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform" loading="lazy" />
                    ) : (
                      <Smartphone className="absolute inset-0 m-auto text-slate-300" size={48} />
                    )}
                    {novidadeIds.has(p.id) && (
                      <span className="absolute top-2 left-2 bg-[#2563EB] text-white text-[9px] uppercase tracking-wider font-bold px-2 py-1 rounded-sm">
                        NOVIDADE
                      </span>
                    )}
                    {p.stock <= (p.min_stock || 5) && p.stock > 0 && (
                      <span className="absolute top-2 right-2 bg-amber-500 text-white text-[9px] uppercase tracking-wider font-bold px-2 py-1 rounded-sm">
                        Últimas
                      </span>
                    )}
                    {p.stock <= 0 && (
                      <>
                        <div className="absolute inset-0 bg-white/70 flex items-center justify-center">
                          <span className="bg-red-600 text-white text-xs uppercase tracking-wider font-bold px-3 py-1.5 rounded-sm shadow-md rotate-[-6deg]">
                            Esgotado
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                  <div className="p-3 flex flex-col flex-1">
                    <div className="text-xs text-slate-500 mb-1">{p.category}</div>
                    <div className="font-semibold text-sm leading-tight line-clamp-2 min-h-[40px] text-slate-800">{p.name}</div>
                    <div className="font-display font-bold text-lg text-[#2563EB] mt-2">{brl(p.sale_price)}</div>
                    {p.stock <= 0 ? (
                      <a
                        href={wa(`Olá! O *${p.name}* está esgotado. Quando chega novamente?`)}
                        target="_blank" rel="noreferrer"
                        data-testid={`catalog-notify-${p.id}`}
                        className="mt-2 flex items-center justify-center gap-1 border border-slate-300 hover:border-[#2563EB] hover:text-[#2563EB] text-slate-700 text-xs font-semibold py-2 rounded-sm transition-colors"
                      >
                        Me avise quando chegar
                      </a>
                    ) : (
                      <a
                        href={wa(`Olá! Tenho interesse: *${p.name}* (${brl(p.sale_price)})`)}
                        target="_blank" rel="noreferrer"
                        data-testid={`catalog-buy-${p.id}`}
                        className="mt-2 flex items-center justify-center gap-1 bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-semibold py-2 rounded-sm transition-colors"
                      >
                        <ShoppingCart size={12} /> Comprar
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Services */}
        </main>
      </div>

      {/* Footer */}
      <footer className="bg-[#0A1628] text-slate-400 mt-10">
        <div className="max-w-7xl mx-auto px-4 py-8 grid md:grid-cols-3 gap-6 text-sm">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <img src={settings.store_logo_url || LOGO_URL} alt="UP CEL Quilombo" className="w-10 h-10 rounded-sm object-cover" />
              <div className="font-display font-bold text-white text-lg">{settings.store_name || "UP CEL Quilombo"}</div>
            </div>
            <p className="text-xs">Celulares, Acessórios e Assistência Técnica</p>
          </div>
          <div>
            <div className="text-white font-semibold mb-2">Contato</div>
            <div className="flex items-center gap-1.5 text-xs mb-1"><Phone size={12} /> {settings.phone || "(49) 99812-2884"}</div>
            {settings.address && <div className="flex items-center gap-1.5 text-xs mb-1"><MapPin size={12} /> {settings.address}</div>}
            {settings.instagram_handle && <div className="flex items-center gap-1.5 text-xs"><Instagram size={12} /> @{settings.instagram_handle}</div>}
          </div>
          <div>
            <div className="text-white font-semibold mb-2">Atendimento</div>
            {settings.whatsapp_number && (
              <a href={wa("Olá!")} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 bg-emerald-500 hover:bg-emerald-600 text-white px-3 py-2 rounded-sm text-xs font-semibold">
                <Phone size={12} /> Falar no WhatsApp
              </a>
            )}
          </div>
        </div>
        <div className="border-t border-white/10 py-4 text-center text-xs">
          © 2026 {settings.store_name || "UP CEL Quilombo"} • Todos os direitos reservados
        </div>
      </footer>
    </div>
  );
}
